import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Pagination,
  Paper,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Close, Groups } from '@mui/icons-material';

import type {
  CreateTravelerDto,
  TravelerDocumentMatchDto,
  TravelerResponseDto,
  UpdateTravelerDto,
} from '@bandido/shared/dtos';

import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

import {
  useAssociateCaseTraveler,
  useCaseTravelers,
  useCaseTravelerSuggestions,
  useCreateCaseTraveler,
  useLinkCaseTravelerByDocument,
  useRemoveCaseTraveler,
  useTravelerDocumentMatches,
  useUpdateCaseTraveler,
} from '../queries';
import {
  isDocumentLookup,
  linkVariablesForMatch,
} from '../travelerDocumentLookup';
import { travelerDisplayName } from '../travelerForm';
import {
  travelerLoadState,
  travelerPageWithinRange,
} from '../travelerUiState';
import { TravelerCard } from './TravelerCard';
import { TravelerDocumentMatchList } from './TravelerDocumentMatchList';
import { TravelerFormDialog } from './TravelerFormDialog';

const PAGE_SIZE = 8;

interface Props {
  open: boolean;
  caseId: number;
  agencyId: number;
  hasClient: boolean;
  onClose: () => void;
}

export const CaseTravelersModal = ({
  open,
  caseId,
  agencyId,
  hasClient,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const narrowViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const { addToast } = useSnackBar();
  const [search, setSearch] = useState('');
  const [rosterPage, setRosterPage] = useState(1);
  const [suggestionsPage, setSuggestionsPage] = useState(1);
  const [editingTraveler, setEditingTraveler] =
    useState<TravelerResponseDto>();
  const [formOpen, setFormOpen] = useState(false);
  const [createDocumentNumber, setCreateDocumentNumber] = useState<string>();
  const [formError, setFormError] = useState<string>();
  const [travelerToRemove, setTravelerToRemove] =
    useState<TravelerResponseDto>();

  const roster = useCaseTravelers({
    caseId,
    search,
    currentPage: rosterPage,
    pageSize: PAGE_SIZE,
    enabled: open,
  });
  const suggestions = useCaseTravelerSuggestions({
    caseId,
    search,
    currentPage: suggestionsPage,
    pageSize: PAGE_SIZE,
    enabled: open && hasClient,
  });
  // Un número escrito en el buscador también se busca en toda la agencia, por
  // documento exacto. Por nombre sólo se busca en el caso y sus conocidos.
  const documentSearch =
    hasClient && isDocumentLookup(search) ? search.trim() : '';
  const documentMatches = useTravelerDocumentMatches(
    caseId,
    documentSearch,
    open && Boolean(documentSearch),
  );
  const agencyMatches = (documentMatches.data ?? []).filter(
    (match) => match.relation === 'other_client',
  );
  const createTraveler = useCreateCaseTraveler(caseId);
  const updateTraveler = useUpdateCaseTraveler(caseId);
  const associateTraveler = useAssociateCaseTraveler(caseId);
  const linkByDocument = useLinkCaseTravelerByDocument(caseId);
  const removeTraveler = useRemoveCaseTraveler(caseId);

  const loadState = useMemo(
    () => travelerLoadState([roster.error, suggestions.error]),
    [roster.error, suggestions.error],
  );
  const busy =
    associateTraveler.isLoading ||
    linkByDocument.isLoading ||
    removeTraveler.isLoading ||
    createTraveler.isLoading ||
    updateTraveler.isLoading;

  useEffect(() => {
    const validPage = travelerPageWithinRange(
      rosterPage,
      roster.data?.pages,
    );
    if (validPage !== rosterPage) setRosterPage(validPage);
  }, [roster.data?.pages, rosterPage]);

  useEffect(() => {
    const validPage = travelerPageWithinRange(
      suggestionsPage,
      suggestions.data?.pages,
    );
    if (validPage !== suggestionsPage) setSuggestionsPage(validPage);
  }, [suggestions.data?.pages, suggestionsPage]);

  const changeSearch = (value: string) => {
    setSearch(value);
    setRosterPage(1);
    setSuggestionsPage(1);
  };

  const openCreate = (documentNumber?: string) => {
    setEditingTraveler(undefined);
    setCreateDocumentNumber(documentNumber);
    setFormError(undefined);
    setFormOpen(true);
  };

  const openEdit = (traveler: TravelerResponseDto) => {
    setEditingTraveler(traveler);
    setCreateDocumentNumber(undefined);
    setFormError(undefined);
    setFormOpen(true);
  };

  const saveTraveler = async (
    input: CreateTravelerDto | UpdateTravelerDto,
  ) => {
    setFormError(undefined);
    try {
      if (editingTraveler) {
        await updateTraveler.mutateAsync({
          travelerId: editingTraveler.id,
          input: input as UpdateTravelerDto,
        });
      } else {
        await createTraveler.mutateAsync(input as CreateTravelerDto);
      }
      setFormOpen(false);
      addToast({
        message: t('travelers.feedback.saved'),
        color: 'success',
      });
    } catch (_error) {
      setFormError(t('travelers.feedback.saveError'));
    }
  };

  const takeAgencyMatch = async (match: TravelerDocumentMatchDto) => {
    try {
      const traveler = await linkByDocument.mutateAsync({
        travelerId: match.travelerId,
        documentNumber: documentSearch,
      });
      addToast({
        message: t('travelers.feedback.added', {
          name: travelerDisplayName(traveler),
        }),
        color: 'success',
      });
    } catch (_error) {
      addToast({
        message: t('travelers.documentMatches.linkError'),
        color: 'error',
      });
    }
  };

  /**
   * Desde el formulario: en un alta, usa la ficha existente en vez de crear
   * otra; en una edición, la ficha existente reemplaza en el caso a la que se
   * estaba editando (que sigue siendo conocida del cliente). El servidor hace
   * las dos cosas en una sola transacción.
   */
  const takeExistingFromForm = async (
    match: TravelerDocumentMatchDto,
    documentNumber: string,
  ) => {
    setFormError(undefined);
    try {
      const traveler = await linkByDocument.mutateAsync(
        linkVariablesForMatch(match, documentNumber, editingTraveler?.id),
      );
      const replaced =
        Boolean(editingTraveler) && editingTraveler?.id !== traveler.id;
      addToast({
        message: replaced
          ? t('travelers.documentMatches.swapped', {
            name: travelerDisplayName(traveler),
          })
          : t('travelers.feedback.added', {
            name: travelerDisplayName(traveler),
          }),
        color: 'success',
      });
      setFormOpen(false);
    } catch (_error) {
      setFormError(t('travelers.documentMatches.linkError'));
    }
  };

  const associate = async (traveler: TravelerResponseDto) => {
    try {
      await associateTraveler.mutateAsync(traveler.id);
      addToast({
        message: t('travelers.feedback.added', {
          name: travelerDisplayName(traveler),
        }),
        color: 'success',
      });
    } catch (_error) {
      addToast({
        message: t('travelers.feedback.addError'),
        color: 'error',
      });
    }
  };

  const remove = async () => {
    if (!travelerToRemove) return;
    try {
      await removeTraveler.mutateAsync(travelerToRemove.id);
      addToast({
        message: t('travelers.feedback.removed'),
        color: 'success',
      });
      setTravelerToRemove(undefined);
    } catch (_error) {
      addToast({
        message: t('travelers.feedback.removeError'),
        color: 'error',
      });
    }
  };

  // Sólo hace falta la sección si hay algo que la lista del caso y de los
  // conocidos no muestra: fichas de otros clientes, nadie con ese documento,
  // la búsqueda en curso o su error.
  const showAgencySection =
    Boolean(documentSearch) &&
    (documentMatches.isLoading ||
      documentMatches.isError ||
      agencyMatches.length > 0 ||
      documentMatches.data?.length === 0);

  const renderAgencyMatches = () => {
    if (documentMatches.isLoading) {
      return <Skeleton variant="rounded" height={56} sx={{ mt: 1 }} />;
    }
    if (documentMatches.isError) {
      return (
        <Alert severity="error" sx={{ mt: 1 }}>
          {t('travelers.documentMatches.lookupError')}
        </Alert>
      );
    }
    if (agencyMatches.length > 0) {
      return (
        <>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            {t('travelers.documentMatches.sectionHint')}
          </Typography>
          <TravelerDocumentMatchList
            matches={agencyMatches}
            actionLabel={t('travelers.documentMatches.use')}
            busy={busy}
            onUse={takeAgencyMatch}
          />
        </>
      );
    }
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mt={1}
      >
        <Typography variant="body2" color="text.secondary">
          {t('travelers.documentMatches.none')}
        </Typography>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => openCreate(documentSearch)}
          disabled={busy}
        >
          {t('travelers.documentMatches.createWithDocument', {
            number: documentSearch,
          })}
        </Button>
      </Box>
    );
  };

  const renderList = (
    items: TravelerResponseDto[] | undefined,
    mode: 'roster' | 'suggestion',
    loading: boolean,
  ) => {
    if (loading) {
      return (
        <Box display="flex" flexDirection="column" gap={1}>
          <Skeleton variant="rounded" height={88} />
          <Skeleton variant="rounded" height={88} />
        </Box>
      );
    }
    if (!items?.length) {
      return (
        <Typography color="text.secondary" variant="body2" py={2}>
          {search
            ? t('travelers.states.noResults')
            : mode === 'roster'
              ? t('travelers.states.emptyRoster')
              : t('travelers.states.emptySuggestions')}
        </Typography>
      );
    }
    return (
      <Box display="flex" flexDirection="column" gap={1.5}>
        {items.map((traveler) => (
          <TravelerCard
            key={traveler.id}
            traveler={traveler}
            mode={mode}
            busy={busy}
            onEdit={openEdit}
            onAssociate={associate}
            onRemove={setTravelerToRemove}
          />
        ))}
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        fullScreen={narrowViewport}
        maxWidth="lg"
      >
        <DialogTitle display="flex" alignItems="center" gap={1}>
          <Groups color="primary" />
          <Box flex={1}>
            <Typography variant="h6">{t('travelers.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('travelers.subtitle')}
            </Typography>
          </Box>
          <IconButton aria-label={t('common.close')} onClick={onClose}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ minHeight: 480 }}>
          {loadState === 'forbidden' ? (
            <Alert severity="warning">
              {t('travelers.states.noPermission')}
            </Alert>
          ) : loadState === 'error' && roster.isError ? (
            <Alert severity="error" action={<Button onClick={() => roster.refetch()}>{t('common.retry')}</Button>}>
              {t('travelers.states.loadError')}
            </Alert>
          ) : (
            <>
              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                gap={2}
                mb={2}
              >
                <Box flex={1}>
                  <SearchInput
                    placeholder={t('travelers.search')}
                    onSearch={changeSearch}
                  />
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => openCreate()}
                  disabled={!hasClient}
                >
                  {t('travelers.actions.create')}
                </Button>
              </Box>

              {showAgencySection && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('travelers.documentMatches.sectionTitle')}
                  </Typography>
                  {renderAgencyMatches()}
                </Paper>
              )}

              {!hasClient && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('travelers.states.noClient')}
                </Alert>
              )}

              <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                gap={2}
              >
                <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('travelers.sections.roster', {
                      count: roster.data?.count ?? 0,
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {t('travelers.sections.rosterHint')}
                  </Typography>
                  {renderList(roster.data?.items, 'roster', roster.isLoading)}
                  {(roster.data?.pages ?? 0) > 1 && (
                    <Pagination
                      sx={{ mt: 2 }}
                      page={rosterPage}
                      count={roster.data?.pages ?? 1}
                      onChange={(_event, page) => setRosterPage(page)}
                      size="small"
                    />
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('travelers.sections.known', {
                      count: suggestions.data?.count ?? 0,
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {t('travelers.sections.knownHint')}
                  </Typography>
                  {!hasClient ? null : suggestions.isError ? (
                    <Alert severity="error">
                      {t('travelers.states.suggestionsError')}
                    </Alert>
                  ) : (
                    renderList(
                      suggestions.data?.items,
                      'suggestion',
                      suggestions.isLoading,
                    )
                  )}
                  {(suggestions.data?.pages ?? 0) > 1 && (
                    <Pagination
                      sx={{ mt: 2 }}
                      page={suggestionsPage}
                      count={suggestions.data?.pages ?? 1}
                      onChange={(_event, page) => setSuggestionsPage(page)}
                      size="small"
                    />
                  )}
                </Paper>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {formOpen && (
        <TravelerFormDialog
          open
          agencyId={agencyId}
          caseId={caseId}
          traveler={editingTraveler}
          initialDocumentNumber={createDocumentNumber}
          isSaving={createTraveler.isLoading || updateTraveler.isLoading}
          usingExisting={linkByDocument.isLoading}
          error={formError}
          onClose={() => setFormOpen(false)}
          onSave={saveTraveler}
          onUseExisting={takeExistingFromForm}
        />
      )}

      <Dialog
        open={Boolean(travelerToRemove)}
        onClose={() => setTravelerToRemove(undefined)}
        maxWidth="xs"
      >
        <DialogTitle>{t('travelers.remove.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('travelers.remove.description', {
              name: travelerToRemove
                ? travelerDisplayName(travelerToRemove)
                : '',
            })}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('travelers.remove.keepProfile')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTravelerToRemove(undefined)}>
            {t('common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={remove}
            disabled={removeTraveler.isLoading}
          >
            {t('travelers.actions.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
